import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { supplierService } from '../../../services/supplierService';


const SupplierAssignmentPanel = ({ suppliers, countries, onRefresh }) => {
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    countryId: '',
    roleId: '',
    regionAccess: []
  });
  const [loading, setLoading] = useState(false);

  const handleAssignTerritory = async () => {
    if (!selectedSupplier || !assignmentForm?.countryId || !assignmentForm?.roleId) {
      alert('Veuillez remplir tous les champs requis');
      return;
    }

    try {
      setLoading(true);
      await supplierService?.assignSupplierToTerritory(
        selectedSupplier?.id,
        assignmentForm?.roleId,
        assignmentForm?.countryId,
        assignmentForm?.regionAccess
      );
      
      alert('Territoire assigné avec succès');
      setShowAssignModal(false);
      setAssignmentForm({ countryId: '', roleId: '', regionAccess: [] });
      onRefresh?.();
    } catch (error) {
      console.error('Error assigning territory:', error);
      alert('Erreur lors de l\'assignation du territoire');
    } finally {
      setLoading(false);
    }
  };

  const getRoleTypeBadge = (roleType) => {
    const badges = {
      production_supplier: { color: 'bg-blue-100 text-blue-800', label: 'Production' },
      dentist_supplier: { color: 'bg-green-100 text-green-800', label: 'Dentiste' }
    };
    return badges?.[roleType] || { color: 'bg-gray-100 text-gray-800', label: roleType };
  };

  return (
    <div className="space-y-6">
      {/* Supplier List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Affectation des Fournisseurs</h2>
          <p className="text-sm text-gray-600 mt-1">Gérez les assignations territoriales des fournisseurs</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fournisseur</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Territoires</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Machines</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {suppliers?.map(supplier => (
                <tr key={supplier?.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{supplier?.companyName || supplier?.fullName}</div>
                      <div className="text-sm text-gray-500">{supplier?.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {supplier?.roleAssignments?.map((assignment, idx) => {
                        const badge = getRoleTypeBadge(assignment?.roleType);
                        return (
                          <span key={idx} className={`px-2 py-1 text-xs font-medium rounded ${badge?.color}`}>
                            {badge?.label}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {supplier?.roleAssignments
                        ?.filter(a => a?.isActive && a?.countryName)
                        ?.map((assignment, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                            <Icon name="MapPin" className="w-3 h-3" />
                            {assignment?.countryCode}
                          </span>
                        ))}
                      {supplier?.roleAssignments?.filter(a => a?.isActive && a?.countryName)?.length === 0 && (
                        <span className="text-sm text-gray-400">Aucun territoire</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Icon name="Settings" className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{supplier?.machineCount || 0}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => {
                        setSelectedSupplier(supplier);
                        setShowAssignModal(true);
                      }}
                      className="flex items-center gap-2 px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    >
                      <Icon name="Plus" className="w-4 h-4" />
                      Assigner
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {suppliers?.length === 0 && (
            <div className="text-center py-12">
              <Icon name="Users" className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucun fournisseur trouvé</p>
            </div>
          )}
        </div>
      </div>
      {/* Assignment Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Assigner un Territoire</h3>
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="X" className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fournisseur</label>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900">{selectedSupplier?.companyName || selectedSupplier?.fullName}</p>
                  <p className="text-sm text-gray-500">{selectedSupplier?.email}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pays *</label>
                <select
                  value={assignmentForm?.countryId}
                  onChange={(e) => setAssignmentForm(prev => ({ ...prev, countryId: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sélectionner un pays</option>
                  {countries?.map(country => (
                    <option key={country?.id} value={country?.id}>
                      {country?.countryName} ({country?.countryCode})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de Rôle *</label>
                <select
                  value={assignmentForm?.roleId}
                  onChange={(e) => setAssignmentForm(prev => ({ ...prev, roleId: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sélectionner un rôle</option>
                  <option value="production_supplier">Fournisseur Production</option>
                  <option value="dentist_supplier">Dentiste Fournisseur</option>
                </select>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowAssignModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleAssignTerritory}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Assignation...' : 'Assigner'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierAssignmentPanel;